/**
 * ItemCostSummaryCell — the compact form of an item's cost-and-purchasing
 * picture, for inventory-item LISTS (one row per canonical item, never one row
 * per vendor product).
 *
 * Same labels and same rules as ItemPurchaseSummary, less vertical space:
 *
 *   desktop (layout='row')      Current inventory cost · Preferred · Best price
 *   mobile  (layout='card')     stacked, with the item name staying the
 *                               loudest thing on the card
 *
 * Vendor details are deliberately quieter than the item name, and no slot ever
 * falls back to a bare "Price" or "Vendor". A cost we cannot compute reads
 * "Cost unavailable"; it never reads $0.00.
 *
 * Props:
 *   summary       - buildItemCostSummary() output
 *   layout        - 'row' (desktop table cell) | 'card' (mobile)
 *   loading       - neutral placeholders instead of values
 *   onViewOptions - opens the purchase-options content DIRECTLY. When given,
 *                   the option count becomes the button, so a tap reaches the
 *                   vendors in one step.
 */
import React from 'react';
import {
  formatUnitPrice,
  describePurchaseOptions,
  bestPriceLabel,
} from '../utils/itemCostSummary';

const C = {
  ink: '#3E2723',
  body: '#5A4A42',
  muted: '#8B7B73',
  link: '#6A4E3C',
};

const styles = {
  row: { display: 'flex', flexWrap: 'wrap', gap: '10px 18px', alignItems: 'flex-start', minWidth: 0 },
  card: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  slot: { minWidth: 0 },
  label: {
    fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: C.muted, margin: 0, whiteSpace: 'nowrap',
  },
  cost: { fontSize: '0.95rem', fontWeight: 700, color: C.ink, marginTop: 1 },
  costMuted: { fontSize: '0.82rem', fontWeight: 700, color: C.muted, marginTop: 1 },
  method: { fontSize: '0.7rem', color: C.muted, marginTop: 1 },
  value: { fontSize: '0.85rem', color: C.body, marginTop: 1, wordBreak: 'break-word' },
  valueMuted: { fontSize: '0.82rem', color: C.muted, marginTop: 1, fontStyle: 'italic' },
  optionsBtn: {
    display: 'inline-flex', alignItems: 'center', minHeight: 32,
    padding: '4px 10px', borderRadius: 8, marginTop: 2,
    background: '#fff', color: C.link, border: '1.5px solid #D5C9C0',
    fontFamily: 'inherit', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
    textAlign: 'left', whiteSpace: 'normal',
  },
  optionsText: { fontSize: '0.82rem', color: C.body, marginTop: 1 },
  skeleton: (w) => ({ display: 'block', width: w, height: 12, borderRadius: 6, background: '#EFE7DF', marginTop: 4 }),
};

const Slot = ({ label, children }) => (
  <div style={styles.slot}>
    <dt style={styles.label}>{label}</dt>
    <dd style={{ margin: 0 }}>{children}</dd>
  </div>
);

export default function ItemCostSummaryCell({
  summary,
  layout = 'row',
  loading = false,
  onViewOptions,
}) {
  if (!summary) return null;
  const { countUnit, currentCost, preferred, best, optionsSummary, item } = summary;

  const optionsLabel = describePurchaseOptions(optionsSummary);
  const optionsNode = onViewOptions ? (
    <button
      type="button"
      style={styles.optionsBtn}
      onClick={(event) => { event.stopPropagation(); onViewOptions(); }}
      aria-label={`View purchase options for ${item?.description || 'this item'} — ${optionsLabel}`}
      data-testid="icsc-view-options"
    >
      {optionsLabel}
    </button>
  ) : (
    <div style={styles.optionsText} data-testid="icsc-options">{optionsLabel}</div>
  );

  return (
    <dl
      style={layout === 'card' ? styles.card : styles.row}
      data-testid="item-cost-summary-cell"
    >
      <Slot label="Current inventory cost">
        {loading ? (
          <span style={styles.skeleton(70)} aria-hidden="true" />
        ) : currentCost.available ? (
          <>
            <div style={styles.cost} data-testid="icsc-cost">
              {formatUnitPrice(currentCost.amount, countUnit)}
            </div>
            <div style={styles.method}>{currentCost.method.label}</div>
          </>
        ) : (
          <div style={styles.costMuted} data-testid="icsc-cost-unavailable">Cost unavailable</div>
        )}
      </Slot>

      <Slot label="Purchase options">
        {loading ? <span style={styles.skeleton(90)} aria-hidden="true" /> : optionsNode}
      </Slot>

      <Slot label="Preferred vendor">
        {loading ? (
          <span style={styles.skeleton(80)} aria-hidden="true" />
        ) : preferred?.vendorLabel ? (
          <div style={styles.value} data-testid="icsc-preferred">{preferred.vendorLabel}</div>
        ) : (
          <div style={styles.valueMuted}>None selected</div>
        )}
      </Slot>

      <Slot label={bestPriceLabel(best)}>
        {loading ? (
          <span style={styles.skeleton(90)} aria-hidden="true" />
        ) : best ? (
          <div style={styles.value} data-testid="icsc-best">
            {[formatUnitPrice(best.unitCost, countUnit), best.vendorLabel]
              .filter(Boolean).join(' at ')}
            {best.stale ? ' · may be outdated' : ''}
          </div>
        ) : (
          <div style={styles.valueMuted}>No comparable prices</div>
        )}
      </Slot>
    </dl>
  );
}
